import React, { useState } from 'react';
import { useHistory } from 'react-router';
import { track } from '@amplitude/analytics-browser';
import {
  Box,
  Button,
  Card,
  Link,
  Radio,
  Spinner,
  Text,
} from '@nimbus-ds/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AppLayout from 'App/components/AppLayout';
import { ROUTES } from 'App/Routes/constants';
import { ErrorMessage, WarningModal } from 'commons/components';
import {
  pickWireTransferSettlementFee,
  useFeatureFlag,
  useIdentityValidation,
  useIsMobile,
  useSettlementFees,
  useToast,
} from 'commons/hooks';
import {
  configurationService,
  FeatureFlag,
  merchantService,
} from 'commons/services';
import { SettlementPeriodType } from 'commons/types';
import { formatPercentage, useTranslationWithPrefix } from 'commons/utils';
import LeaveD0Modal from './LeaveD0Modal';
import SettlementPeriodInfo from './SettlementPeriodInfo';
import SettlementPeriodSkeleton from './SettlementPeriodSkeleton';

function SettlementPeriod(): JSX.Element {
  const { t } = useTranslationWithPrefix('settlementPeriod');
  const { showToastSuccess, showToastError } = useToast();
  const history = useHistory();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [buttonLoading, setButtonLoading] = useState(false);
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [leaveD0ModalOpen, setLeaveD0ModalOpen] = useState(false);
  // Undefined until the merchant picks a radio; until then the saved period is shown.
  const [settlementPeriod, setSettlementPeriod] =
    useState<SettlementPeriodType>();
  const identityValidation = useIdentityValidation();
  const {
    loading: settlementFeesLoading,
    settlementFees,
    wireTransferSettlementFees,
    usedFallback,
  } = useSettlementFees();
  const settlementPeriodQuery = useQuery(
    ['settlementPeriod'],
    () => configurationService.getSettlementPeriod(),
    {
      retry: false,
    },
  );
  const balanceQuery = useQuery(
    ['balance'],
    () => merchantService.getBalance(),
    {
      retry: false,
    },
  );
  const updateSettlementPeriod = useMutation({
    mutationFn: (periodToSave: SettlementPeriodType) => {
      setButtonLoading(true);
      return configurationService.putSettlementPeriod(periodToSave);
    },
    onSuccess: (_data, periodToSave) => {
      queryClient.setQueryData(['settlementPeriod'], periodToSave);
      showToastSuccess(t('success'));
      track('pago-nube-guardar-plazo-de-retiro', {
        settlement: periodToSave,
      });
      history.push(ROUTES.CONFIG);
    },
    onError: () => {
      setButtonLoading(false);
      showToastError(t('error'));
    },
  });

  const qRModoFlag = useFeatureFlag(FeatureFlag.FS_PAGONUBE_QR_MODO_ENABLED);
  const d30Flag = useFeatureFlag(
    FeatureFlag.FS_PAGONUBE_D30_AVAILABLE_FOR_ARGENTINA_STORE,
  );

  if (
    settlementFeesLoading ||
    settlementPeriodQuery.isLoading ||
    balanceQuery.isLoading ||
    identityValidation.isLoading ||
    qRModoFlag.loading ||
    d30Flag.loading
  ) {
    return <SettlementPeriodSkeleton />;
  }
  if (
    settlementPeriodQuery.isError ||
    identityValidation.isError ||
    (usedFallback && settlementPeriodQuery.data === 0)
  ) {
    return (
      <AppLayout title={t('title')} backLinkPath={ROUTES.CONFIG}>
        <ErrorMessage />
      </AppLayout>
    );
  }

  const savedSettlementPeriod = settlementPeriodQuery.data;
  const wireTransferSettlementFee = pickWireTransferSettlementFee(
    wireTransferSettlementFees,
    savedSettlementPeriod,
  );
  const selectedSettlementPeriod = settlementPeriod ?? savedSettlementPeriod;
  const changed = selectedSettlementPeriod !== savedSettlementPeriod;

  const changeSettlementPeriod = (aSettlementPeriod: SettlementPeriodType) => {
    setSettlementPeriod(aSettlementPeriod);
  };

  const redirectBack = () => {
    history.push(ROUTES.CONFIG);
  };

  const submit = () => {
    if (!changed || selectedSettlementPeriod === undefined) {
      showToastSuccess(t('success'));
      redirectBack();
      return;
    }

    if (savedSettlementPeriod === 0 && !leaveD0ModalOpen) {
      setLeaveD0ModalOpen(true);
      return;
    }

    updateSettlementPeriod.mutate(selectedSettlementPeriod);
  };

  const redirectOrWarningModal = () => {
    if (changed) {
      setWarningModalOpen(true);
    } else {
      redirectBack();
    }
  };

  const blockButtons =
    identityValidation.data.status === 'pending' ||
    identityValidation.data.status === 'pending_documentation' ||
    identityValidation.data.status === 'pending_review';

  const Buttons = (
    <Box gap="4" display="flex" flexDirection="row">
      <Button onClick={redirectOrWarningModal} disabled={buttonLoading}>
        {t('cancel')}
      </Button>
      <Button
        appearance="primary"
        onClick={submit}
        disabled={buttonLoading || blockButtons}
      >
        {buttonLoading && <Spinner size="small" />}
        {t('save')}
      </Button>
    </Box>
  );

  const MobileSaveLink = (
    <Link
      appearance="neutral"
      onClick={submit}
      disabled={buttonLoading || blockButtons}
      as="button"
      textDecoration="none"
    >
      {buttonLoading && <Spinner size="small" />}
      {t('save')}
    </Link>
  );

  // Both rules test the saved period, not the selected radio: a store on 30 or
  // on 0 must keep seeing its option after clicking another one by mistake.
  // A store can never come back to 0 by itself, so 0 is offered only while saved.
  const offeredSettlementFees = settlementFees.filter(
    ({ settlementPeriod: feeSettlementPeriod }) => {
      if (feeSettlementPeriod === 0) return savedSettlementPeriod === 0;
      if (feeSettlementPeriod === 30)
        return d30Flag.enabled || savedSettlementPeriod === 30;
      return true;
    },
  );

  const settlementRadios = offeredSettlementFees.map(
    ({ fee, settlementPeriod: feeSettlementPeriod }) => {
      const label =
        feeSettlementPeriod === 0
          ? t('withdraw-same-day', { fee: formatPercentage(fee) })
          : feeSettlementPeriod === 1
            ? t('withdraw-1-day', { fee: formatPercentage(fee) })
            : t('withdraw-n-days', {
                fee: formatPercentage(fee),
                days: feeSettlementPeriod,
              });

      return (
        <Radio
          label={label}
          name={feeSettlementPeriod.toString()}
          checked={feeSettlementPeriod === selectedSettlementPeriod}
          onChange={() => changeSettlementPeriod(feeSettlementPeriod)}
          key={feeSettlementPeriod}
          disabled={blockButtons}
        />
      );
    },
  );

  return (
    <AppLayout
      title={t('title')}
      subtitle={t('subtitle')}
      footer={Buttons}
      navBarItems={isMobile ? MobileSaveLink : undefined}
      backLinkPath={ROUTES.CONFIG}
    >
      <Box display="flex" flexDirection="column" gap="4">
        <SettlementPeriodInfo
          identityValidationStatus={identityValidation.data.status}
        />
        <Card>
          <Box display="flex" flexDirection="column" gap="6">
            <Box display="flex" flexDirection="column" gap="4">
              <Text fontWeight="medium" color="neutral-textHigh">
                {t(
                  qRModoFlag.enabled
                    ? 'creditDebitCardAndWallet'
                    : 'creditDebitCard',
                )}
              </Text>
              {settlementRadios}
            </Box>
            {wireTransferSettlementFee ? (
              <Box display="flex" flexDirection="column" gap="4">
                <Text fontWeight="medium" color="neutral-textHigh">
                  {t('transfer')}
                </Text>
                <Text color="neutral-textHigh">
                  {wireTransferSettlementFee.settlementPeriod === 0
                    ? t('withdraw-same-day', {
                        fee: formatPercentage(wireTransferSettlementFee.fee),
                      })
                    : t('transferSettlement', {
                        fee: formatPercentage(wireTransferSettlementFee.fee),
                      })}
                </Text>
              </Box>
            ) : null}
          </Box>
        </Card>
      </Box>
      <WarningModal
        exitButtonDisabled={buttonLoading}
        onDismiss={() => setWarningModalOpen(false)}
        onExit={redirectBack}
        open={warningModalOpen}
      />
      <LeaveD0Modal
        open={leaveD0ModalOpen}
        confirmDisabled={buttonLoading}
        onConfirm={submit}
        onDismiss={() => setLeaveD0ModalOpen(false)}
      />
    </AppLayout>
  );
}

SettlementPeriod.Skeleton = SettlementPeriodSkeleton;
export default SettlementPeriod;
